/*
 * A linha da lista de tópicos: [Tópico | Postadores | Respostas |
 * Visualizações | Atividade] no desktop; avatar, título e contagem no celular.
 *
 * Mexe só nas ItemLists do DiscussionListItem: o que outras extensões põem em
 * infoItems (o excerto do sticky, a digitação do realtime) e em statsItems
 * continua aparecendo, só que no lugar que o tema dá a eles.
 */
import app from 'flarum/forum/app';
import { extend, override } from 'flarum/common/extend';
import DiscussionListItem from 'flarum/forum/components/DiscussionListItem';
import IndexPage from 'flarum/forum/components/IndexPage';
import Link from 'flarum/common/components/Link';
import Avatar from 'flarum/common/components/Avatar';
import Tooltip from 'flarum/common/components/Tooltip';
import listItems from 'flarum/common/helpers/listItems';
import highlight from 'flarum/common/helpers/highlight';
import abbreviateNumber from 'flarum/common/utils/abbreviateNumber';
import classList from 'flarum/common/utils/classList';
import { truncate } from 'flarum/common/utils/string';
import { forumBool, t } from '../utils/settings';
import { fullDate, shortAge } from '../utils/dates';
import { discussionBadges } from '../utils/tags';
import { activityCold, discussionViews, repliesHeat, viewsHeat } from './metrics';
import { viewsNumber } from '../utils/views';
import { typingPill } from '../motion/typing';

/** Chaves do infoItems que a linha nova desenha em outro lugar (ou dispensa). */
const MOVED_INFO = ['tags', 'terminalPost', 'excerpt'];

/**
 * O MinimalDiscussionListItem (resultados do modal de busca) herda desta
 * classe; lá a linha compacta do core continua valendo. A classe CSS é o único
 * jeito estável de reconhecê-lo — o módulo pode nem estar carregado. (Por isso
 * o tema não estende elementAttrs: a checagem chama elementAttrs e entraria em
 * laço; o CSS da linha mira `.CanelaList .DiscussionListItem`.)
 */
function isMinimal(item) {
  return String(item.elementAttrs().className || '').includes('MinimalDiscussionListItem');
}
function heatClass(heat, kind = 'heat') {
  return heat && forumBool('canelaListHeat', true) ? `CanelaCell--${kind}-${heat}` : undefined;
}
function posterAvatar(user, role, className = '') {
  if (!user) return null;
  const label = `${user.displayName()}: ${role}`;
  return m(Tooltip, {
    text: label
  }, m(Link, {
    href: app.route.user(user),
    className: classList('CanelaPosters-avatar', className),
    "aria-label": label
  }, m(Avatar, {
    user: user,
    title: ""
  })));
}
export default function installListItem() {
  // Ao voltar de um tópico, a linha dele acende e apaga em 2,5s. O core já guarda qual
  // foi (lastDiscussion) e rola até ela; o tema só acende a linha.
  extend(IndexPage.prototype, 'oncreate', function () {
    const last = this.lastDiscussion;
    if (!last) return;
    const row = this.element?.querySelector(`li[data-id="${last.id()}"] .DiscussionListItem`);
    if (!row) return;
    row.classList.add('CanelaListItem--highlighted');
    row.addEventListener('animationend', () => row.classList.remove('CanelaListItem--highlighted'), {
      once: true
    });
  });
  extend(DiscussionListItem.prototype, 'contentItems', function (items) {
    if (isMinimal(this)) return;

    // O avatar grande da esquerda e a coluna de estatísticas do core saem:
    // os postadores e os números vão para colunas próprias.
    items.remove('author');
    items.remove('stats');

    // Só no celular (o CSS esconde no desktop): o avatar de 48px.
    items.add('canelaAvatar', avatarView(this), 100);
    if (forumBool('canelaListPosters', true)) {
      items.add('canelaPosters', postersView(this), 60);
    }
    items.add('canelaReplies', repliesView(this), 50);
    if (forumBool('canelaListViews', true)) {
      // Sempre desenhada: quem decide se a coluna aparece é a lista inteira
      // (classe no DiscussionList), não cada linha.
      items.add('canelaViews', viewsView(this), 40);
    }
    items.add('canelaActivity', activityView(this), 30);
  });
  override(DiscussionListItem.prototype, 'mainView', function (original) {
    if (isMinimal(this)) return original();
    const discussion = this.attrs.discussion;
    const jumpTo = this.getJumpTo();
    const info = this.infoItems();
    const searching = !!this.attrs.params.q;

    // O excerto do fixado é opcional; o da busca, não — ele é o resultado.
    const excerpt = info.has('excerpt') && (searching || forumBool('canelaListExcerpts', true)) ? excerptView(this, info.get('excerpt'), searching) : null;
    MOVED_INFO.forEach(key => info.remove(key));

    // O "alguém está digitando" do realtime: a pílula do tema, com o rótulo dele.
    if (info.has('realtimeTyping')) {
      const original = info.get('realtimeTyping');
      info.setContent('realtimeTyping', typingPill(String(original?.attrs?.['aria-label'] || t('list.typing'))));
    }

    // statsItems de extensões (curtidas, por exemplo) não têm coluna própria:
    // vão para a linha das etiquetas. A contagem de respostas já é coluna.
    const extraStats = this.statsItems();
    extraStats.remove('replyCount');
    const badgeList = discussion.badges();

    // O cadeado (fechado) vem antes do alfinete (fixado).
    if (badgeList.has('locked')) badgeList.setPriority('locked', 1000);
    const badges = badgeList.toArray();
    return m("div", {
      className: "DiscussionListItem-main CanelaTopic"
    }, m("h2", {
      className: "DiscussionListItem-title CanelaTopic-titleLine"
    }, badges.length > 0 && m("span", {
      className: "CanelaTopic-status"
    }, badges), m(Link, {
      href: app.route.discussion(discussion, jumpTo),
      className: "CanelaTopic-title"
    }, highlight(discussion.title(), this.highlightRegExp)), unreadView(this)), m("div", {
      className: "CanelaTopic-meta"
    }, discussionBadges(discussion), !info.isEmpty() && m("ul", {
      className: "DiscussionListItem-info CanelaTopic-info"
    }, listItems(info.toArray())), !extraStats.isEmpty() && m("span", {
      className: "CanelaTopic-stats"
    }, extraStats.toArray())), excerpt ? m("div", {
      className: "CanelaTopic-excerpt"
    }, excerpt) : null);
  });
}

/**
 * Bolinha de "tópico novo" (nunca aberto) ou pílula com as não lidas. As
 * duas marcam como lido ao clicar, igual à contagem do core.
 */
function unreadView(item) {
  const discussion = item.attrs.discussion;
  if (!app.session.user || !discussion.isUnread()) return null;
  const neverRead = !discussion.lastReadPostNumber();
  const label = neverRead ? t('list.new_topic') : t('list.unread_count', {
    count: discussion.unreadCount()
  });
  return m("button", {
    type: "button",
    className: classList('Button--ua-reset CanelaUnread', neverRead ? 'CanelaUnread--new' : 'CanelaUnread--count'),
    title: `${label}: ${t('list.mark_read')}`,
    "aria-label": `${label}: ${t('list.mark_read')}`,
    onclick: event => {
      event.preventDefault();
      item.markAsRead();
    }
  }, neverRead ? null : abbreviateNumber(discussion.unreadCount()));
}

/**
 * O excerto do fixado termina com "… ler mais" quando foi cortado. O
 * flarum/sticky entrega o texto já
 * truncado dentro de um div; o tema refaz o corte para pôr o link na mesma
 * linha. O da busca (trecho com destaque) fica como veio.
 */
function excerptView(item, original, searching) {
  const discussion = item.attrs.discussion;
  const text = discussion.attribute('firstPostExcerpt');
  if (searching || typeof text !== 'string' || !text) return original;
  const limit = 175;
  if (text.length <= limit) return text;
  return [truncate(text, limit), ' ', m(Link, {
    href: app.route.discussion(discussion),
    className: "CanelaTopic-excerptMore"
  }, t('list.read_more'))];
}
function avatarView(item) {
  const user = item.attrs.author || item.attrs.discussion.user();
  return m("div", {
    className: "CanelaListItem-avatar"
  }, user ? m(Link, {
    href: app.route.user(user),
    "aria-label": user.displayName()
  }, m(Avatar, {
    user: user,
    title: ""
  })) : m(Avatar, {
    user: null,
    title: ""
  }));
}
function postersView(item) {
  const discussion = item.attrs.discussion;
  const author = item.attrs.author || discussion.user();
  const last = discussion.replyCount() ? discussion.lastPostedUser() : null;
  const relevantPost = item.attrs.params.q ? discussion.mostRelevantPost() : null;
  const relevant = relevantPost ? relevantPost.user() : null;

  // O "último postador": quem respondeu por último
  // ou, sem respostas, o próprio autor. É o único avatar que sobra no tablet.
  const latest = last || author;
  const seen = new Set();
  const entries = [];
  const push = (user, role) => {
    if (!user || seen.has(user.id())) return;
    seen.add(user.id());
    entries.push({
      user,
      role
    });
  };
  push(author, t('list.original_poster'));
  push(relevant, t('list.relevant_poster'));
  push(last, t('list.most_recent_poster'));
  const posters = entries.map(({
    user,
    role
  }, index) => {
    const isLatest = !!latest && user === latest;
    return posterAvatar(user, role, classList({
      'CanelaPosters-avatar--latest': isLatest,
      // O autor que também postou por último brilha, se houver outros
      // avatares ao lado (`.avatar.latest:not(.single)`).
      'CanelaPosters-avatar--glow': isLatest && index === 0 && entries.length > 1
    }));
  });
  return m("div", {
    className: "CanelaCell CanelaCell--posters CanelaPosters"
  }, posters);
}
function repliesView(item) {
  const count = item.attrs.discussion.replyCount() ?? 0;
  return m("div", {
    className: classList('CanelaCell CanelaCell--replies', heatClass(repliesHeat(count)))
  }, m("span", {
    "aria-hidden": "true"
  }, abbreviateNumber(count)), m("span", {
    className: "visually-hidden"
  }, t('list.replies_a11y', {
    count
  })));
}
function viewsView(item) {
  const views = discussionViews(item.attrs.discussion);
  return m("div", {
    className: classList('CanelaCell CanelaCell--views', views !== null && heatClass(viewsHeat(views)))
  }, views === null ? null : viewsNumber(views));
}
function activityView(item) {
  const discussion = item.attrs.discussion;
  const created = discussion.createdAt();
  const last = discussion.lastPostedAt() || created;
  const title = [t('list.created_at', {
    date: fullDate(created)
  }), discussion.replyCount() ? t('list.last_reply_at', {
    date: fullDate(last)
  }) : null].filter(Boolean).join('\n');
  return m("div", {
    className: classList('CanelaCell CanelaCell--activity', heatClass(activityCold(discussion), 'cold')),
    title: title
  }, m(Link, {
    href: app.route.discussion(discussion, discussion.lastPostNumber() ?? undefined),
    className: "CanelaActivity"
  }, shortAge(last)));
}
flarum.reg.add('ramon-canela', 'forum/list/listItem', installListItem);